/**
 * shell/ModelCatalogSection.tsx —— 「模型」设置面（**A1-5 前端五格**）。
 *
 * 真源（102，**逐字搬运**）：`components/settings/ModelCatalogSection.vue`（391 行）——
 *   · 三列三责（大王 20260922 原话）：`id` 只对系统负责（**服务端生成，本面不填不显示**，仅作行 key）／
 *     `display_name` 只对人负责（「显示名」格，可空 ⇒ 后端回落 `api_model`）／`api_model` 只对厂商负责
 *     （「调用名」格，**新增必填**）＋ `base_url`（厂商地址，**新增必填**）＋「API 密钥（真值 · **只写 · 零回显**）」
 *     ＋「推理强度」（选项**单一源**＝`theme/modelCatalog.ts`，与后端白名单同集）
 *   · **五格、无 id 格**；编辑态留空字段＝不改（`api_key` 打开表单恒为空，绝不回填既有密钥）
 *   · 列表行：只显示**显示名**（缺 ⇒ 回落 id；**系统 id 不上屏**）＋ 推理强度**常显**（空＝「不指定」灰态）
 *     ＋ **可点＝编辑本行**；另「编辑」按钮
 *   · 删除：**二次确认**（首点进入「确认删除」态，**3 秒内**再点才真删；超时复位）；**无批量、无多选**，
 *     作用对象恒为**当前选中行**；说明文案＝同一常量（`title`／上屏提示共用）
 *   · 报障修正（★20260922）：缺必填项点保存**原为静默 return** ⇒ 现**显式提示**
 *     （`请先填写：…（新增必填）`，`role="alert"`，**零请求**）
 *   · 20260921 令：分组列表**限高滚动**，录入表单与动作行留在滚动区外（常驻可见）
 *
 * 纪律：Vue 语法→React/TS（`defineProps`→props／`defineEmits`→回调 props／`defineExpose({closeForm})`
 *   →`useImperativeHandle`）；**不搬** Vue 专属语法与 scoped 样式（样式见 `styles/model-catalog.css`）。
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { REASONING_EFFORT_OPTIONS } from "../theme/modelCatalog";
import "../styles/model-catalog.css";

export type ModelRow = { id: string; name?: string; effort?: string };
export type ModelGroup = { key: string; label: string; items?: ModelRow[] };
export type ModelFormValues = {
  apiModel: string;
  displayName: string;
  baseUrl: string;
  apiKey: string;
  thinkingEffort: string;
};

/** 删除模型入口的**用户可见说明**（单一事实源：`title`／上屏提示共用）。 */
export const DELETE_HINT =
  "删除模型对所有用户生效（物理删除）；若其他用户正把它当默认模型会被拒绝";

const EMPTY_FORM: ModelFormValues = { apiModel: "", displayName: "", baseUrl: "", apiKey: "", thinkingEffort: "" };

export interface ModelCatalogProps {
  /** `catalog.groups`（形状 [{key,label,items:[{id,name,effort}]}]） */
  groups: ModelGroup[];
  /** 当前默认模型 id（= prefs.default_model） */
  current?: string;
  /** 提交中（宿主置位；表单禁用防重复提交） */
  busy?: boolean;
  onSetDefault?: (id: string) => void;
  onAddModel?: (values: ModelFormValues) => void;
  onUpdateModel?: (values: ModelFormValues & { id: string }) => void;
  onDeleteModel?: (id: string) => void;
  /** 宿主侧错误（落库失败/被拒；**可见提示**——禁静默失败） */
  modelError?: string;
}

export interface ModelCatalogHandle {
  /** 保存成功 ⇒ 收起表单（宿主**命令式回程**；成功才调用，失败保留已填内容）。 */
  closeForm: () => void;
}

const ModelCatalogSection = forwardRef<ModelCatalogHandle, ModelCatalogProps>(function ModelCatalogSection(
  { groups, current = "", busy = false, modelError = "", onSetDefault, onAddModel, onUpdateModel, onDeleteModel },
  ref,
) {
  // ── 二次确认（防误删）：3 秒内再点才真删；超时自动复位（无批量、无多选）──
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  /** `editingId` 非空 ⇒ 编辑既有条目（PUT；留空字段＝不改）；空 ⇒ 新增（POST） */
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState<ModelFormValues>(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  /** 新增（无参）：全空表单；编辑（传行对象）：回填**本面可见的两格**（显示名／推理强度），
   *  其余留空＝不改；**密钥恒为空（只写、零回显）**。 */
  const openForm = useCallback((m?: ModelRow | null) => {
    setEditingId((m && m.id) || "");
    setForm({
      apiModel: "",
      displayName: (m && m.name) || "",
      baseUrl: "",
      apiKey: "",
      thinkingEffort: (m && m.effort) || "",
    });
    setFormError("");
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormError("");
    setFormOpen(false);
    setEditingId("");
    setForm(EMPTY_FORM);
  }, []);

  useImperativeHandle(ref, () => ({ closeForm }), [closeForm]);
  useEffect(() => () => { if (confirmTimer.current) clearTimeout(confirmTimer.current); }, []);

  function onDeleteClick() {
    if (!current) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    setConfirmDelete(false);
    onDeleteModel?.(current);
  }

  /** 提交：新增要求「调用名 ＋ base_url」齐才发（**缺项 ⇒ 显式提示、零请求**）；
   *  编辑只发本面两格 ＋ 其余非空项（留空＝不改）。 */
  function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    const { apiModel, displayName, baseUrl, apiKey, thinkingEffort } = form;
    if (editingId) {
      setFormError("");
      onUpdateModel?.({ id: editingId, apiModel, displayName, baseUrl, apiKey, thinkingEffort });
      return;
    }
    const missing: string[] = [];
    if (!apiModel) missing.push("调用名");
    if (!baseUrl) missing.push("base_url");
    if (missing.length) {
      setFormError(`请先填写：${missing.join("、")}（新增必填）`);
      return;
    }
    setFormError("");
    onAddModel?.({ apiModel, displayName, baseUrl, apiKey, thinkingEffort });
  }

  return (
    <div className="model-catalog" data-testid="model-catalog">
      {/* 宿主侧失败（落库被拒/网络错）⇒ 可见提示（禁静默：点保存无反应＝最坏体验） */}
      {modelError ? (
        <p className="model-catalog__error xg-hint" role="alert" data-testid="model-save-error">{modelError}</p>
      ) : null}
      <div className="model-catalog__list" data-testid="model-catalog-list">
        {groups.map((g) => (
          <section key={g.key} className="model-catalog__group" data-provider={g.key} role="group">
            <div className="model-catalog__group-label">
              <span className="model-catalog__group-name">{g.label}</span>
              <span className="model-catalog__group-count">{(g.items || []).length}</span>
            </div>
            {(g.items || []).map((m) => (
              <label key={m.id} className="model-catalog__row" data-model-id={m.id}>
                <input
                  type="radio"
                  className="model-catalog__radio"
                  data-testid="settings-control"
                  name="xg-default-model"
                  value={m.id}
                  checked={m.id === current}
                  onChange={() => onSetDefault?.(m.id)}
                />
                {/* 只显示「对人负责」的那一格；系统 id 不上屏 */}
                <span className="model-catalog__name">{m.name || m.id}</span>
                {/* 推理强度常显（空值＝「不指定」灰态）＋ 可点＝编辑本行 */}
                <button
                  type="button"
                  className={"model-catalog__effort" + (m.effort ? "" : " model-catalog__effort--unset")}
                  data-testid="model-effort-tag"
                  data-effort={m.effort || ""}
                  title={m.effort ? `推理强度：${m.effort}（点此编辑本行）` : "推理强度：不指定（点此编辑本行）"}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openForm(m); }}
                >
                  推理强度 {m.effort || "不指定"}
                </button>
                <button
                  type="button"
                  className="model-catalog__row-act"
                  data-testid="model-row-edit"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openForm(m); }}
                >
                  编辑
                </button>
              </label>
            ))}
          </section>
        ))}
      </div>

      {formOpen && (
        <form
          className="model-catalog__form"
          data-testid="model-entry-form"
          data-mode={editingId ? "edit" : "create"}
          onSubmit={onSubmit}
        >
          <label className="model-catalog__field">调用名（对厂商负责 · 新增必填）
            <input
              value={form.apiModel}
              autoComplete="off"
              placeholder="真正发给厂商的模型名，如 deepseek-v4-flash"
              data-testid="model-entry-api-model"
              onChange={(e) => setForm((f) => ({ ...f, apiModel: e.target.value.trim() }))}
            />
          </label>
          <label className="model-catalog__field">显示名（对人负责 · 可选）
            <input
              value={form.displayName}
              autoComplete="off"
              placeholder="留空 ⇒ 用调用名"
              data-testid="model-entry-name"
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value.trim() }))}
            />
          </label>
          <label className="model-catalog__field">base_url（厂商地址 · 新增必填）
            <input
              value={form.baseUrl}
              autoComplete="off"
              placeholder="https://api.example.com/v1（留空 ⇒ 按域规则推导协议）"
              data-testid="model-entry-base-url"
              onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value.trim() }))}
            />
            {/* ★20260922 尾（大王拍 A）：同厂商多协议时「粘完整端点」是唯一可表达的写法（协议无独立输入格）
                ——本轮 MiMo 两行就是「只填到 /v1」被推成不存在端点而 404（现场实证）。 */}
            <span className="model-catalog__field-hint" data-testid="model-entry-url-hint">
              同厂商多协议时，把「完整端点」粘进来即可表达协议：<code>…/v1/chat/completions</code>（OpenAI 兼容）、<code>…/v1/responses</code>（Responses）、<code>…/anthropic/v1</code>（Anthropic）；只填域名或根路径时按域规则推导（缺省 OpenAI 兼容）。
            </span>
          </label>
          <label className="model-catalog__field">API 密钥（可选 · 只写入、不回显）
            <input
              type="password"
              value={form.apiKey}
              autoComplete="new-password"
              placeholder="留空 ⇒ 使用部署环境已配置的密钥（键名由系统按地址推导）"
              data-testid="model-entry-api-key"
              onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value.trim() }))}
            />
          </label>
          <label className="model-catalog__field">推理强度（可选）
            <select
              value={form.thinkingEffort}
              data-testid="model-entry-effort"
              onChange={(e) => setForm((f) => ({ ...f, thinkingEffort: e.target.value }))}
            >
              {REASONING_EFFORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          {formError && (
            <p className="model-catalog__form-error" data-testid="model-entry-error" role="alert">{formError}</p>
          )}
          <div className="model-catalog__form-actions">
            <button type="submit" className="model-catalog__add-btn" data-testid="model-entry-submit" disabled={busy}>保存</button>
            <button type="button" className="model-catalog__add-btn" data-testid="model-entry-cancel" onClick={closeForm}>取消</button>
          </div>
        </form>
      )}

      <div className="model-catalog__add">
        <button type="button" className="model-catalog__add-btn" data-testid="model-add-model" onClick={() => openForm()}>
          添加模型
        </button>
        <button
          type="button"
          className={"model-catalog__add-btn" + (confirmDelete ? " model-catalog__add-btn--danger" : "")}
          data-testid="model-delete-model"
          disabled={!current}
          title={current ? `${DELETE_HINT}（当前选中：${current}）` : "请先在列表里选中要删除的模型"}
          onClick={onDeleteClick}
        >
          {confirmDelete ? "确认删除" : "删除模型"}
        </button>
      </div>

      <p className="model-catalog__add-hint" data-testid="model-delete-hint">{DELETE_HINT}</p>
    </div>
  );
});

export default ModelCatalogSection;
